import { useId, useState } from 'react'

import type { ModuleDiagnosticsReport } from '@ctcells/api'
import { enumLabel, t } from '@ctcells/i18n'

import { AddModal } from '../../atoms/AddModal.tsx'
import { Button } from '../../atoms/Button.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Icon, type IconName } from '../../atoms/Icon.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import type { ActionGate } from '../access.ts'
import { staleValue } from '../format.ts'
import { FormSection, ModalFooter } from '../formSections.tsx'

import type { ModuleControlStatus } from './moduleControlStatus.ts'

/**
 * 모듈 서랍 「제어」 카드의 조각(eric 2026-10-22 「제어락 필요로 하고. 제어 오른쪽에 정상 칩 두고. 버튼을 … 타이틀만으로 버튼 만들어주고」)
 * — 상태 칩 · 글자만 있는 조작 단추(설명은 툴팁) · 결과 줄.
 *
 * 🔴 **동작 · 권한 · 요청 · 사유 창은 e26ef10 그대로다** — 배치만 바꿨다. 단추는 숨기지 않고 비활성이다.
 * ⚠ 아이콘을 쓰지 않는다 — 글자만이다(eric 같은 날).
 */

/* ═══════════════════════ 상태 칩 ═══════════════════════ */

/**
 * 「제어」 제목 바로 오른쪽 상태 칩(eric 2026-10-22 「제어 오른쪽에 정상 칩 두고」) — `moduleControlStatus` 판정 그대로.
 * 정상은 「정상」, 모르면 `—`(「정상」으로 접지 않는다), 걸린 것은 칩 여럿(위험은 붉은 톤 + 기호). 응답 없는 모듈이면 칩마다 「마지막 값」.
 */
export function ModuleStatusChips({
  status,
  stale,
}: {
  status: ModuleControlStatus
  stale: boolean
}) {
  return (
    <span aria-label={t('column.status')} className="modctl__status" role="group">
      {status.kind === 'unknown' ? (
        <span className="muted">{t('mask.none')}</span>
      ) : status.kind === 'normal' ? (
        <StatusBadge
          label={staleValue(t('enum.safetyState.normal'), stale)}
          status="IDLE"
          tone="ok"
        />
      ) : (
        status.parts.map((part) => (
          <StatusBadge
            key={part.key}
            label={staleValue(part.label, stale)}
            originalValue={part.detail}
            status={part.danger ? 'ERROR' : 'PAUSED'}
            {...(part.danger ? { tone: 'danger' as const } : {})}
          />
        ))
      )}
    </span>
  )
}

/* ═══════════════════════ 조작 단추 ═══════════════════════ */

export interface ControlButtonProps {
  /** 단추 글자(「초기화」) — 이름만 */
  label: string
  /** 마우스를 올리거나 키보드로 닿으면 보이는 설명 — 서버 summary · 정본 용어로 짧게 */
  description: string
  gate: ActionGate
  onClick: () => void
  /** 위험 쪽 조작(트레이 자리 정리 — 멈춘 작업을 폐기한다)은 위험 톤 단추 */
  danger?: boolean | undefined
  /** 설명 뒤에 붙일 안내(「모듈이 허용 명령을 안 보냈습니다…」) — 화면 본문에 적지 않고 툴팁으로 보낸다 */
  note?: string | undefined
  /**
   * 글자 앞 아이콘(앱 아이콘 세트 `Icon` · eric 2026-10-23 「제어 버튼을 아이콘과 타이틀로 깔끔하게」). 장식이다 — 이름은 글자가 말한다.
   * 안 주면 글자만(작업 띠 시작 점검의 조치 단추 등 종전 자리).
   */
  icon?: IconName | undefined
}

/**
 * 조작 단추 — 아이콘 + 글자(eric 2026-10-23 「제어 버튼을 아이콘과 타이틀로 깔끔하게 정리해줘」 · 종전 10-22 「타이틀만으로 버튼 만들어주고.
 * 마우스 호버를 하면 해당 설명이 보이게」의 글자 · 툴팁은 그대로). 아이콘은 장식이라 보조기술에 숨는다(`Icon` 의 `aria-hidden`).
 *
 * - 🔴 설명은 툴팁이다 — 마우스를 올리거나 **키보드 포커스로도** 보인다(`.modctl__tip` · `:hover` · `:focus-visible`).
 *   보조기술은 `aria-describedby` 로 읽는다. ⚠ 네이티브 `title` 은 키보드로 안 보여 쓰지 않았다(그래서 이 자리만 CSS 툴팁이다).
 * - 🔴 비활성은 숨기지 않는다(`aria-disabled`). 사유가 **제어락이면** 툴팁에 붙이지 않는다 — 카드 머리 「제어락 필요」 하나로 충분하다
 *   (보조기술에는 숨김 글자로 남긴다). 다른 사유(권한 없음 · 연결 끊김 · 단독 모드 · 작업 진행 중)는 설명 뒤에 붙인다.
 */
export function ControlButton({
  label,
  description,
  gate,
  onClick,
  danger = false,
  note,
  icon,
}: ControlButtonProps) {
  const tipId = useId()
  const lockId = useId()
  const lockReason = gate.disabled && gate.disabledReason === t('reason.lockRequired')
  const reason = gate.disabled && !lockReason ? gate.disabledReason : undefined
  const tip = [description, note, reason]
    .filter((part) => part !== undefined && part !== '')
    .join(' — ')
  return (
    <span className="modctl__btn">
      <button
        aria-describedby={lockReason ? `${tipId} ${lockId}` : tipId}
        aria-disabled={gate.disabled ? true : undefined}
        className={danger ? 'a-btn a-btn--pill a-btn--danger' : 'a-btn a-btn--pill'}
        onClick={gate.disabled ? undefined : onClick}
        type="button"
      >
        {icon === undefined ? null : <Icon name={icon} size="sm" />}
        <span className="modctl__label">{label}</span>
      </button>
      <span className="modctl__tip" id={tipId} role="tooltip">
        {tip}
      </span>
      {lockReason ? (
        <span className="a-sr" id={lockId}>
          {gate.disabledReason}
        </span>
      ) : null}
    </span>
  )
}

/* ═══════════════════════ 사유를 받는 조작(파지 기억 · 복구 잠김) ═══════════════════════ */

export interface ReasonActionButtonProps {
  /** 단추 · 창 승인 글자(「파지 기억 초기화」) */
  actionLabel: string
  description: string
  /** 창 본문 — 무엇이 바뀌고 무엇이 안 바뀌는지 */
  confirmBody: string
  gate: ActionGate
  /** 보내기. 부르는 즉시 요청이 나간다 — 🔴 사유는 사람이 적은 것 그대로(다듬기만 한다) */
  onSend: (reason: string) => void
  sending?: boolean | undefined
  /** 단추 글자 앞 아이콘(`ControlButton.icon`) — 안 주면 글자만 */
  icon?: IconName | undefined
}

/**
 * 사유를 받는 조작 단추 — 단추는 창만 연다(확인 전 요청 0건). 서버가 `reason` 을 필수로 받는다(`ForgetModuleGripsBody` ·
 * `ResetModuleRecoveryLockBody` — 감사에 그대로 실린다). 창은 누르는 즉시 닫고 결과 · 거절은 카드의 「최근 결과」에 선다.
 */
export function ReasonActionButton({
  actionLabel,
  description,
  confirmBody,
  gate,
  onSend,
  sending = false,
  icon,
}: ReasonActionButtonProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  /* 사유는 승인을 누를 때 잰다(eric 2026-10-27) — 단추는 받은 `gate`(권한 · 락 · 연결 · 상태)만 막는다 */
  const form = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = form.field('reason')

  const close = () => {
    setOpen(false)
    setReason('')
    form.reset()
  }
  const confirmGate: ActionGate = gate

  return (
    <>
      <ControlButton
        description={description}
        gate={gate}
        icon={icon}
        label={actionLabel}
        onClick={() => setOpen(true)}
      />
      {open ? (
        <AddModal busy={sending} onClose={close} size="sm" title={actionLabel}>
          <FormSection>
            <p className="deny">{confirmBody}</p>
            <Field label={t('field.reason')} required {...reasonField.fieldProps}>
              <TextInput
                {...reasonField.inputProps}
                onChange={(value) => {
                  setReason(value)
                  form.change('reason', value)
                }}
                value={reason}
                wide
              />
            </Field>
          </FormSection>
          <ModalFooter>
            <Button onClick={close} size="pill">
              {t('action.cancel')}
            </Button>
            <Button
              size="pill"
              variant="primary"
              {...confirmGate}
              onClick={() => {
                if (confirmGate.disabled) return
                // 바닥 줄은 본문 밖이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
                if (!form.validate(reason, document.body)) return
                const said = reason.trim()
                close()
                onSend(said)
              }}
            >
              {actionLabel}
            </Button>
          </ModalFooter>
        </AddModal>
      ) : null}
    </>
  )
}

/* ═══════════════════════ 결과 줄 ═══════════════════════ */

/** 실패 한 줄 — 「조작 — 서버 문구 (코드 원문)」 */
export function ResultFailure({
  action,
  error,
  errorCode,
}: {
  action: string
  error: string
  errorCode?: string | undefined
}) {
  return (
    <p className="vissue" role="alert">
      {`${action} — ${errorCode === undefined ? error : `${error} (${errorCode})`}`}
    </p>
  )
}

/** 받음 한 줄 — 「조작 — 받았습니다 — 상태는 구독으로 따라옵니다」 */
export function ResultAccepted({ action, text }: { action: string; text: string }) {
  return (
    <p className="small muted" role="status">
      {`${action} — ${text}`}
    </p>
  )
}

/**
 * 진단 결과 — 통과/실패 칩 + 항목마다 `check_id` 원값 · 판정(`enum.diagnostic`).
 * 🔴 `check_id` 는 정적 어휘가 없다(모듈이 런타임 선언) — 번역하지 않는다. `unknown` 을 「통과」로 접지 않는다.
 */
export function DiagnosticsResult({ report }: { report: ModuleDiagnosticsReport }) {
  return (
    <div className="modctl__diag">
      {report.passed === undefined ? null : (
        <StatusBadge
          label={report.passed ? t('moduleCtl.diagPassed') : t('moduleCtl.diagFailed')}
          status={report.passed ? 'IDLE' : 'ERROR'}
          tone={report.passed ? 'ok' : 'danger'}
        />
      )}
      {report.checks === undefined || report.checks.length === 0 ? null : (
        <ul aria-label={t('action.runDiagnostics')} className="modctl__checks">
          {report.checks.map((check) => (
            <li key={check.check_id}>
              <span className="mono">{check.check_id}</span>{' '}
              {check.result === 'fail' ? (
                <b>{enumLabel('enum.diagnostic', check.result)}</b>
              ) : (
                <span className="muted">{enumLabel('enum.diagnostic', check.result)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
